import React, { PropsWithChildren, useEffect, useState } from 'react';

import * as api from './FilesApi';
import * as notifications from 'lib/notifications';
import Timestamp from 'components/Common/Timestamp';
import UserDtoView from 'components/Common/UserDtoView';
import { AttachedFileDto, AttachedFilePermissionType, AttachedFileSortColumn, AttachedFileType, AttachedFileWithPermissionsDto } from 'models/index';
import { createHashRouter, createRoutesFromElements, Route, Link, RouterProvider, useParams, useSearchParams, Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { UploadFileZone } from './UploadFileZone';
import Pager from 'components/Common/Pager';
import { TableHeaderWithColumnType } from './TableHeaderWithColumnType';

import './Files.css';

// Bootstrap
import Table from 'react-bootstrap/Table';

import { GenericSearchWrapper } from 'models';
import { Card, Col, Dropdown, Row } from 'react-bootstrap';

import { Breadcrumbs } from './Breadcrumbs';
import { IFilenameForm, FilenameForm } from './FilenameForm';
import { ChooseFolderForm } from './ChooseFolderForm';
import useFiles, { FilesContextProvider } from './useFiles';
import { isWriteableBy } from './isWriteableBy';
import { clampSortColumn, clampSortOrder } from './clampSortColumn';
import { AttachedFilePermissionForm } from './AttachedFilePermissionForm';
import { getIsPrivateDescription } from './utilities';
import { ContentPageWrapper } from '../Common/ContentPageWrapper';

function humanFileSize(size?: number) {
    if (typeof size === 'undefined' || size === null)
        return;
    var i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

function formatSize(size?: number) {
    return humanFileSize(size);
}

function formatFilename(value: AttachedFileDto) {
    if (value.type === AttachedFileType.File) {
        return value.fileName;
    }
    if (value.type === AttachedFileType.Directory) {
        return <Link to={`../${value.id}`}><i className="bi bi-folder"></i> {value.fileName}</Link>
    }
}

function AttachedFilePermissions(props: { file: AttachedFileWithPermissionsDto }) {
    const file = props.file;
    //console.log("AttachedFilePermissions", file);
    // Om rättigheterna är ärvda från ovanstående folder, så visar vi endast strängen "Rättigheterna är ärvda"
    // Om foldern är privat.
    console.log("AttachedFilePermissions file", file.id);

    return (<>
        <h5>Rättigheter</h5>
        <p>
            {getIsPrivateDescription(file.isPrivate)}
            <br />
            {file.isPermissionInherited === true ? ("Rättigheter ärvs från överliggande objekt.") : ("Rättigheter anges på detta objekt och ärvs nedåt.")}
        </p>
        {isWriteableBy(file) && (<Link to="permissions" relative="path">Ändra rättigheter...</Link>)}
    </>);
}

export interface IAppProps {
    userId: number;
}

export interface IFilesContainerOutletContext {
    setShowFolderActionButton: (show: boolean) => void;
}

function RowItemDropdownMenu(props: {value: AttachedFileWithPermissionsDto, deleteItem: (value: AttachedFileDto) => void,
    renameItem: (value: AttachedFileDto) => void, moveItem: (value: AttachedFileDto) => void}) {
    const value = props.value;
    return (<>
        {/* <ActionDropdownMenu /> */}

        {(isWriteableBy(value) || value.type == AttachedFileType.File) && (
            <Dropdown>
                <Dropdown.Toggle variant="link" id="dropdown-basic" size='sm' style={{ padding: 0 }}>
                    Åtgärder
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    {value.type == AttachedFileType.File && (<Dropdown.Item href={api.attachedFilesGet(value.id)} target="_blank" >Ladda ner</Dropdown.Item>)}
                    {(isWriteableBy(value) && value.type == AttachedFileType.File) && (<Dropdown.Divider />)}
                    {isWriteableBy(value) && (<Dropdown.Item onClick={() => { props.deleteItem(value); }}>Ta bort</Dropdown.Item>)}
                    {isWriteableBy(value) && (<Dropdown.Item onClick={() => { props.renameItem(value); }}>Byt namn</Dropdown.Item>)}
                    {isWriteableBy(value) && (<Dropdown.Item onClick={() => { props.moveItem(value); }}>Flytta...</Dropdown.Item>)}
                </Dropdown.Menu>
            </Dropdown>
        )}
        {/* {
                    value.type == AttachedFileType.File &&
                    <>
                        &nbsp;
                        <a href={api.attachedFilesGet(value.id)} target="_blank">Ladda ner</a>
                    </>
                } */}
    </>)
}

function FilesContainerHome(props: {}) {
    const [searchParams, setSearchParams] = useSearchParams({});
    const pageNrRaw = searchParams.get('page');
    const pageNr = pageNrRaw && parseInt(pageNrRaw);
    const sortColumn = clampSortColumn(searchParams.get('sortColumn')) || AttachedFileSortColumn.FileName;
    const sortOrder = clampSortOrder(searchParams.get('sortOrder'));

    // Common stuff from context
    const { selectedFolder, parentFiles, selectedFolderIsWriteable, renameItem, refreshFileList, files } = useFiles();

    const [fileToMove, setFileToMove] = useState<AttachedFileDto>();
    const [directories, setDirectories] = useState<GenericSearchWrapper<AttachedFileDto>>(null);

    const { setShowFolderActionButton } = useOutletContext<IFilesContainerOutletContext>();
    useEffect(() => {
        setShowFolderActionButton(true);
    });

    useEffect(() => {
        // Update the list of files when the selected folder changes
        console.log("in useEffect() selectedFolder", selectedFolder, "pageNr", pageNr, "sortColumn", sortColumn, "sortOrder", sortOrder);
        if (!selectedFolder)
            return;
        refreshFileList();
    }, [selectedFolder?.id, pageNr, sortColumn, sortOrder]);

    function deleteItem(value: AttachedFileDto) {
        if (!confirm("Är du säker på att du vill ta bort filen?"))
            return;

        //console.log("deleteFile");
        api.fileDelete(value).then(data => {
            //console.log("got data", data);
            notifications.dispatch("Filen togs bort", 5);
            refreshFileList();
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid borttagning", reason, 30);
        });
    }

    function moveItem(value: AttachedFileDto) {
        api.attachedFilesList({ types: [AttachedFileType.Directory], disregardParent: true, pageSize: 100, sortColumn: "FileName" }).then(data => {
            //console.log("got data", data);
            setDirectories(data);
        });
        setFileToMove(value);
    }

    return <>

        {fileToMove && (
            <ChooseFolderForm title={"Flytta fil " + fileToMove.fileName} editedItem={{ fileId: fileToMove.id }}
                directories={directories?.items}
                parents={parentFiles}
                selectedFolder={selectedFolder}
                cancel={() => setFileToMove(null)}
                submit={async (data) => {
                    console.log("onSubmit");
                    console.log(data);
                    try {
                        await api.moveFile({ id: fileToMove.id, destinationId: data.fileId });
                        notifications.dispatch("Filen flyttades", 5);
                        //props.ok();
                        setFileToMove(null);
                        refreshFileList();
                    } catch (e) {
                        notifications.dispatchFromError("Fel vid flytt", e, 30);
                    }
                }}
            />
        )}


        <fieldset>
            <div className="d-none d-sm-block">
            <Table striped hover>
                <thead>
                    <tr>
                        <TableHeaderWithColumnType title='Namn' column={AttachedFileSortColumn.FileName} currentColumn={sortColumn} sortOrder={sortOrder} getColumn={(x) => AttachedFileSortColumn[x]} />
                        <th>Skapad</th>
                        <th>Skapad av</th>
                        <TableHeaderWithColumnType title='Storlek' column={AttachedFileSortColumn.Size} currentColumn={sortColumn} sortOrder={sortOrder} getColumn={(x) => AttachedFileSortColumn[x]} />
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {files && files.items.map((value, index) => (
                        <tr key={value.id}>
                            <td>{formatFilename(value)}</td>
                            <td><Timestamp value={value.created} /></td>
                            <td><UserDtoView value={value.createdBy} /></td>
                            <td>{formatSize(value.size)}</td>
                            <td>
                                <RowItemDropdownMenu value={value} deleteItem={deleteItem} moveItem={moveItem} renameItem={renameItem} />
                            </td>
                        </tr>
                    ))}
                </tbody>

            </Table>
            </div>
            <div className="d-block d-sm-none">
                {files && files.items.map((value, index) => (
                    <Card key={value.id} className='mb-1'>
                    <Card.Body>
                      <Card.Title>{formatFilename(value)}</Card.Title>
                      <Card.Text>
                      Skapad av <UserDtoView value={value.createdBy} /> <br />
                      Skapad <Timestamp value={value.created} /> <br />
                      </Card.Text>
                      <RowItemDropdownMenu value={value} deleteItem={deleteItem} moveItem={moveItem} renameItem={renameItem} />
                    </Card.Body>
                  </Card>
                ))}
            </div>
            {files && files.items.length < 1 && ("Inga filer hittades.")}

            {files && (<Pager queryParameter={"page"} metaData={files.metaData} />)}

        </fieldset>
        <hr />
        <Row>
            <Col sm={12} md={6}>{selectedFolder && <AttachedFilePermissions file={selectedFolder} />}</Col>
            <Col sm={12} md={6}>{selectedFolder && selectedFolderIsWriteable &&
                <>
                    <h5>Lägg till ny fil</h5>
                    <UploadFileZone onFilesUploaded={refreshFileList} selectedFolder={selectedFolder} />
                </>
            }</Col>
        </Row>

    </>;
}

function FilesContainer(props: PropsWithChildren<IAppProps>) {
    const navigate = useNavigate();
    const [showFolderActionButton, setShowFolderActionButton] = useState(true);

    // Common stuff from context
    const { selectedFolder, setSelectedFolder, parentFiles, setParentFiles, selectedFolderIsWriteable, renameItem, refreshFileList, editedFile, setEditedFile } = useFiles();

    let { fileId } = useParams();

    useEffect(() => {
        // Update the list of files when the selected folder changes
        if ((fileId ?? api.emptyGuid) != selectedFolder?.id) {
            console.log("useEffect fileId=", fileId, " selectedFolder=", selectedFolder);
            api.getFile(fileId).then(data => {
                setSelectedFolder(data);
                if (data.type == AttachedFileType.Root) {
                    if (!fileId) {
                        navigate(`${data.id}`); // Redirect to the chosen folder.
                    }
                }
            }).catch((reason) => {
                notifications.dispatchFromError("Fel vid hämtning av vald mapp", reason, 15);
                setSelectedFolder(null);
            });

            api.getParents(fileId).then(data => {
                setParentFiles(data);
            }).catch((reason) => {
                notifications.dispatchFromError("Fel vid hämtning av parent-data", reason, 15);
                setParentFiles(null);
            });
        }
    }, [fileId]);

    function newItem(): IFilenameForm {
        const result = {
        } as IFilenameForm;
        return result;
    }

    function addNewDirectory() {
        setNewDirectory(newItem());
    }
    const [newDirectory, setNewDirectory] = useState<IFilenameForm>();

    return (
        <div className="row">
            <div className="col-sm-12">

                {newDirectory && (
                    <FilenameForm title={"Ny mapp"} editedItem={newDirectory}
                        cancel={() => setNewDirectory(null)}
                        submit={async (data) => {
                            console.log("onSubmit");
                            console.log(data);
                            try {
                                await api.createDirectory({ directoryName: data.filename, parentId: selectedFolder?.id });
                                notifications.dispatch("Mappen skapades", 5);
                                //props.ok();
                                setNewDirectory(null);
                                refreshFileList();
                            } catch (e) {
                                notifications.dispatchFromError("Fel vid sparande", e, 30);
                            }
                        }}
                    />
                )}

                {editedFile && (
                    <FilenameForm title={"Byt namn"} editedItem={{ filename: editedFile.fileName }}
                        cancel={() => setEditedFile(null)}
                        submit={async (data) => {
                            console.log("onSubmit");
                            console.log(data);
                            try {
                                await api.renameFile({ name: data.filename, id: editedFile.id });
                                notifications.dispatch("Namnet byttes", 5);
                                //props.ok();
                                setEditedFile(null);
                                refreshFileList();
                                if (editedFile.id == selectedFolder.id) {
                                    const newFolder = await api.getFile(selectedFolder.id);
                                    setSelectedFolder(newFolder);
                                }
                            } catch (e) {
                                notifications.dispatchFromError("Fel vid namnbyte", e, 30);
                            }
                        }}
                    />
                )}

                <div className="d-flex flex-row bd-highlight mb-3">
                    <div className="me-2 pt-1">
                        Du är här:
                    </div>
                    <div className="pt-1">
                        <Breadcrumbs currentFile={selectedFolder} parentFiles={parentFiles} />
                    </div>
                    <div className="ms-auto">
                        {selectedFolderIsWriteable && showFolderActionButton && (
                            <Dropdown>
                                <Dropdown.Toggle variant="secondary" id="dropdown-basic" size='sm'>
                                    Åtgärder
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => { addNewDirectory(); }}>Skapa mapp</Dropdown.Item>
                                    <Dropdown.Item disabled={selectedFolder.type !== AttachedFileType.Directory} onClick={() => { renameItem(selectedFolder); }}>Byt namn</Dropdown.Item>
                                    <Dropdown.Item onClick={() => { changePermissionsForSelectedFolder(); }}>Ändra rättigheter...</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </div>
                </div>

                <Outlet context={{ showFolderActionButton, setShowFolderActionButton }} />

            </div>

        </div>
    );

    function changePermissionsForSelectedFolder() {
        navigate(`${fileId}/permissions`, { relative: "path" });
    }
}

function FilesContainerWithRouter(props: IAppProps) {
    const router = createHashRouter(
        createRoutesFromElements(
            <>
                <Route
                    path="/"
                    element={<FilesContextProvider><FilesContainer userId={props.userId} /></FilesContextProvider>}>

                    <Route
                        element={<><FilesContainerHome /></>}
                        path=":fileId"
                    >

                    </Route>,
                    <Route
                        element={<><AttachedFilePermissionForm /></>}
                        path="/:fileId/permissions"
                    />
                </Route>
            </>
        ), {
        // basename:"/Data/Klient/Filer.aspx"
    });

    return (
        <ContentPageWrapper userId={props.userId}>
            <RouterProvider router={router} />
        </ContentPageWrapper>
    );
}

export default FilesContainerWithRouter;
